import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../blog/blogFirebase";

// Firestore is the single source of truth for clusters
// (services/blog/clusters/clusterRepository.js). Admins have read access via
// firestore.rules, so — exactly as useKeywordRefresh does for the audit log —
// this subscribes directly instead of paying for a callable round-trip. The
// live subscription also means a manual refresh repaints the table the moment
// saveClusters() commits, with no reload.
const CLUSTERS_COLLECTION = "blog_keyword_clusters";

/** Firestore Timestamp | ISO string | Date → Date | null. */
function toDate(value) {
  if (!value) return null;
  const date = typeof value.toDate === "function" ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Firestore doc → view model.
 * Mirrors hydrate() in clusterRepository, minus the RegExp compilation: the
 * admin only ever displays titlePatterns, so keeping them as strings avoids
 * shipping a parser (and a possible throw) into the browser.
 */
function toCluster(doc) {
  const data = doc.data() || {};
  return {
    ...data,
    id: doc.id,
    keywords: Array.isArray(data.keywords) ? data.keywords : [],
    titlePatterns: Array.isArray(data.titlePatterns) ? data.titlePatterns : [],
    lastRefreshedAt: toDate(data.lastRefreshedAt),
    updatedAt: toDate(data.updatedAt),
  };
}

/** Bare term for a keyword record, tolerating the legacy string shape. */
export function keywordTerm(kw) {
  if (typeof kw === "string") {
    // Legacy annotated string, e.g. "birthday cartoon (vol 2400, KD 0)".
    return kw.replace(/\(.*?\)/g, "").replace(/\s+/g, " ").trim();
  }
  return kw?.term || "";
}

/** Roll the collection up into the figures the header StatCard reports. */
function summarise(clusters) {
  let keywords = 0;
  let volume = 0;
  let ranked = 0;

  for (const cluster of clusters) {
    for (const kw of cluster.keywords) {
      keywords += 1;
      if (typeof kw?.volume === "number") volume += kw.volume;
      if (kw?.position !== null && kw?.position !== undefined) ranked += 1;
    }
  }

  return { clusters: clusters.length, keywords, volume, ranked };
}

/**
 * Live view of blog_keyword_clusters, priority-ordered — the same order the
 * generation prompt reads them in, so the screen matches what the model sees.
 *
 * An empty collection is surfaced as an empty list rather than an error: the
 * repository treats "unseeded" as a genuine failure for generation, but for a
 * read-only admin screen it is simply nothing to show yet.
 */
export function useKeywordClusters() {
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const q = query(collection(db, CLUSTERS_COLLECTION), orderBy("priority"));

    const unsub = onSnapshot(
      q,
      (snap) => {
        setClusters(snap.docs.map(toCluster));
        setLoading(false);
        setError("");
      },
      (err) => {
        console.error("[useKeywordClusters] snapshot error:", err);
        setError(err.message || "Could not read keyword clusters.");
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const totals = useMemo(() => summarise(clusters), [clusters]);

  return { clusters, loading, error, totals };
}
